import { SiteSettings } from '@/types/database'
import defaultConfig from './config'

// Default currency rates to use when server data is unavailable
const DEFAULT_CURRENCY_RATES: Record<string, number> = {
  USD: 1.1,
  EUR: 1,
  GBP: 0.85,
  CAD: 1.4,
  AUD: 1.5,
  NZD: 1.6,
  CHF: 1.05,
  SEK: 10.5,
  PLN: 4.5,
  // Add more defaults as needed
};

// Store a runtime cache of current settings with last fetch timestamp
const runtimeCache: {
  locales?: string[];
  defaultLocale?: string;
  currencyRates?: Record<string, number>;
  lastFetchTime?: number;
} = {
  // Initialize with default values to avoid undefined issues
  locales: defaultConfig.locales,
  defaultLocale: defaultConfig.defaultLocale,
  currencyRates: DEFAULT_CURRENCY_RATES,
  lastFetchTime: 0
};

// Cache invalidation time - refresh data after this many milliseconds
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Server-only: Gets the i18n configuration from site settings
 * This should only be used in server components
 */
export async function getI18nConfig() {
  // Check if cache needs refreshing
  const now = Date.now();
  const cacheExpired = !runtimeCache.lastFetchTime || (now - runtimeCache.lastFetchTime > CACHE_TTL);
  
  if (cacheExpired) {
    
    // Import getApiSiteSettings from API-safe module that doesn't rely on server components
    const { getApiSiteSettings } = await import('@/app/[locale]/utils/site-settings-api');
    
    // Fetch site settings
    const siteSettings = await getApiSiteSettings();

    // Merge currency rates properly for the runtime cache
    const mergedCurrencyRates = {
      ...DEFAULT_CURRENCY_RATES,
      ...(siteSettings.currency_conversion_rates || {})
    };

    // Update runtime cache
    runtimeCache.locales = siteSettings.locales || defaultConfig.locales;
    runtimeCache.defaultLocale = siteSettings.default_locale || defaultConfig.defaultLocale;
    runtimeCache.currencyRates = mergedCurrencyRates;
    runtimeCache.lastFetchTime = now;
    
  }
  
  return {
    // Use site settings if available, otherwise fall back to defaults
    locales: runtimeCache.locales || defaultConfig.locales,
    defaultLocale: runtimeCache.defaultLocale || defaultConfig.defaultLocale,
    
    // Pathnames need to be configured in config.ts directly
    // as they require complex mapping and are not easily storable in DB
    pathnames: defaultConfig.pathnames,
    
    // Create a convertPrice function using rates from settings
    convertPrice: (basePrice: number, targetCurrency: string): number => {
      const rates = runtimeCache.currencyRates || DEFAULT_CURRENCY_RATES;
      return basePrice * (rates[targetCurrency] || 1);
    }
  }
}

/**
 * Gets current currency rates from runtime cache or defaults
 * This is used for client-side conversion without needing to hit the database
 */
export function getCurrencyRates(debug = false): Record<string, number> {
  // Check if cache should be refreshed
  const now = Date.now();
  const cacheExpired = !runtimeCache.lastFetchTime || (now - runtimeCache.lastFetchTime > CACHE_TTL);
  
  if (cacheExpired) {
    if (debug) {
      //
    }
    
    // We're running in a browser environment, so we need to trigger a refresh
    if (typeof window !== 'undefined') {
      // Client-side: trigger async refresh but return current cache
      clearSettingsCache().catch(err => {
        console.error('Failed to refresh currency rates:', err);
      });
    } else {
      // Server-side: we can try to immediately refresh the rates since we're in a server context
      // This is a bit of a hack but helps ensure we have fresh rates
      try {
        // Schedule an immediate async refresh
        setTimeout(async () => {
          try {
            const { getApiSiteSettings } = await import('@/app/[locale]/utils/site-settings-api');
            const siteSettings = await getApiSiteSettings();
            
            if (siteSettings.currency_conversion_rates) {
              // Only update rates, not the whole cache
              runtimeCache.currencyRates = {
                ...DEFAULT_CURRENCY_RATES,
                ...siteSettings.currency_conversion_rates
              };
              runtimeCache.lastFetchTime = Date.now();
            }
          } catch (e) {
            console.error('Failed to refresh currency rates immediately:', e);
          }
        }, 0);
      } catch (e) {
        console.error('Failed to set up immediate currency refresh:', e);
      }
    }
  }
  
  return runtimeCache.currencyRates || DEFAULT_CURRENCY_RATES;
}

/**
 * Client-side currency conversion function that uses cached rates
 */
export function convertPrice(basePrice: number, targetCurrency: string): number {
  const rates = getCurrencyRates();
  return basePrice * (rates[targetCurrency] || 1);
}

/**
 * Client-safe function to update i18n config in runtime
 * This does not access the database and can be used in client components
 */
export function updateI18nConfig(settings: SiteSettings): void {
  // Update runtime cache
  if (settings.locales) {
    runtimeCache.locales = settings.locales;
  }
  
  if (settings.default_locale) {
    runtimeCache.defaultLocale = settings.default_locale;
  }
  
  if (settings.currency_conversion_rates) {
    // Merge with defaults instead of replacing entirely
    runtimeCache.currencyRates = {
      ...DEFAULT_CURRENCY_RATES,
      ...settings.currency_conversion_rates
    };
  }
  
  // Update the timestamp when we manually update settings
  runtimeCache.lastFetchTime = Date.now();
}

/**
 * Clears the runtime cache and forces a refresh from the database
 * Can be used when currency rates or other settings need to be refreshed
 */
export async function clearSettingsCache(): Promise<void> {
  // Reset the cache to defaults
  runtimeCache.currencyRates = { ...DEFAULT_CURRENCY_RATES };
  // Reset the timestamp to force refresh
  runtimeCache.lastFetchTime = 0;
  
  try {
    // Import getApiSiteSettings and refresh the settings
    const { getApiSiteSettings } = await import('@/app/[locale]/utils/site-settings-api');
    const siteSettings = await getApiSiteSettings();
    
    // Update runtime cache with fresh data
    runtimeCache.locales = siteSettings.locales || defaultConfig.locales;
    runtimeCache.defaultLocale = siteSettings.default_locale || defaultConfig.defaultLocale;
    
    // Merge currency rates
    if (siteSettings.currency_conversion_rates) {
      runtimeCache.currencyRates = {
        ...DEFAULT_CURRENCY_RATES,
        ...siteSettings.currency_conversion_rates
      };
    }
    
    // Set the fetch timestamp
    runtimeCache.lastFetchTime = Date.now();
  } catch {
    // If refreshing fails, keep using defaults - no need to log error
    runtimeCache.lastFetchTime = Date.now();
  }
}

/**
 * Generates static paths for a given pattern using localized slugs
 * This ensures each locale uses the correct localized URL structure
 * 
 * @returns Array of locale objects with properly localized paths
 */
export async function generateLocalizedStaticPaths() {
  // Get the latest i18n configuration
  const config = await getI18nConfig();
  
  // Simply return the locales - paths will be resolved by middleware
  return config.locales.map(locale => ({ locale }));
} 